/**
 * @file Parametres de rendu : camera inclinee (~50 deg), outline inverted-hull, toon 2 paliers, lumiere, FX, qualite.
 */
import type { RenderConfig } from './types';

export const RENDER: RenderConfig = {
  camera: {
    fovDeg: 30,
    pitchDeg: 50, // angle de la camera par rapport a l'horizontale (~45-55 deg, comme l'original)
    yawDeg: 0,
    fitWidth: 24,
    followLerp: 6,
    maxDpr: 2,
  },
  outline: {
    method: 'invertedHull',
    color: '#1f1638', // violet nuit : plus de profondeur que le brun
    thicknessPx: 3,
    thinPx: 1.6,
  },
  // 2 paliers = bande d'ombre nette ; shadowTint teinte la lumiere ambiante (faces a l'ombre) et les ombres de contact
  toon: { steps: 2, shadowTint: '#3b2f7a', rimStrength: 0.12 },
  // lumiere plus directionnelle : ~22 % d'ambiance (au lieu de ~40 %) pour donner du volume
  lighting: {
    ambient: 0.45,
    sunIntensity: 1.6,
    sunDir: [-0.4, 1, 0.5],
    ambientTintMix: 0.3,
    contactShadowOpacity: 0.32,
    // etalonnage par decor : soleil a peine teinte + ombres colorees (complementaires du sol), volontairement subtil
    grade: {
      forest: { sun: '#fff5de', shade: '#2c4f86' },
      desert: { sun: '#ffeacb', shade: '#7a3c6e' },
      tundra: { sun: '#f1f7ff', shade: '#34508f' },
      autumn: { sun: '#ffe9d2', shade: '#6a3566' },
      blossom: { sun: '#fff3f6', shade: '#5b3d92' },
      'hub:spring': { sun: '#fff7e6', shade: '#3b4a8a' },
      'hub:summer': { sun: '#fff1d6', shade: '#3a3f86' },
      'hub:autumn': { sun: '#ffe9d2', shade: '#6a3566' },
      'hub:winter': { sun: '#f1f7ff', shade: '#34508f' },
    },
  },
  fx: { maxParticles: 400, maxFloatingTexts: 40, confettiCount: 120 },
  // Post-traitement : bloom seulement en qualite haute (et hors eco) ; vignette CSS partout (gratuite)
  post: {
    bloom: { enabled: true, strength: 0.35, radius: 0.4, threshold: 1.0, resolutionScale: 0.5 },
    vignette: { enabled: true, inner: 0.55, color: 'rgba(31,22,56,0.55)', opacity: 1 },
  },
  // Reglage Qualite : 'low' = image a 1x (net mais moins fin, bien plus leger pour le GPU), pas de halo (bloom), ~65 % des particules ;
  // 'high' = jusqu'a 2x, halo, toutes les particules. Le mode Eco s'ajoute par-dessus (moins d'effets encore).
  quality: {
    low: { maxDpr: 1, shadows: false, bloom: false, fxScale: 0.65 },
    high: { maxDpr: 2, shadows: false, bloom: true, fxScale: 1 },
  },
};

/** Parametres de l'animation procedurale du chat (valeurs par defaut ; pilotent CatAnimator). */
export const CAT_ANIM = {
  idle: { bobAmp: 0.04, bobHz: 1.6, tailSwayDeg: 14, tailHz: 1.1, earTwitchDeg: 10, earTwitchEvery: [2.5, 6] as [number, number], breatheScale: 0.015 },
  walk: { stepHz: 5, legSwingDeg: 35, bobAmp: 0.09, tailSwayDeg: 26, earBounceDeg: 8, leanDeg: 6 },
  shoot: { kickbackUnits: 0.25, kickbackRecoverSeconds: 0.12, bodyRecoilDeg: 4, flashSeconds: 0.06, aimTurnSpeedDegPerSec: 720 },
  hit: { squashScale: 0.85, seconds: 0.15 },
} as const;
